import { z } from 'zod';

import type { MiceCorporatePath } from './mice';
import { MiceTravelerFlightSchema, MiceTravelerWarningSchema } from './mice_invite_flights';
import { MiceInviteResponseSchema } from './mice_invite_response';
import {
  MiceMoneySchema,
  MiceNamedRefSchema,
  NumericIdSchema,
  SelfServiceActionRequestSchema,
} from './mice_refs';

// `mice.ts` importa daqui (contagens na view): não importe valor de `mice`/`mice_self_service`,
// fecharia um ciclo (tipo pode). Os primitivos compartilhados vêm da folha `mice_refs` (e os voos
// do viajante, da folha `mice_invite_flights`).

export const MiceParticipantPendingSchema = z.enum([
  'email_not_found',
  'cost_center_missing',
  'cost_center_not_found',
  'motive_not_found',
  'project_not_found',
]);
export type MiceParticipantPending = z.infer<typeof MiceParticipantPendingSchema>;

/** Um sentido da viagem como o organizador o vê no pedido: o voo que seria comprado (o pedido onde
 *  o viajante escolheu outro, o recomendado onde não mudou), o recomendado e a diferença de preço
 *  (nunca o total). Todos os sentidos da viagem vêm: o organizador aprova a compra inteira. */
export const MiceApprovalDirectionSchema = z.object({
  direction: z.enum(['OUTWARD', 'RETURN']),
  /** O viajante escolheu outro voo neste sentido. */
  changed: z.boolean(),
  /** O voo que seria comprado; null num sentido sem escolha e sem recomendado (nada a comprar). */
  requested: MiceTravelerFlightSchema.nullable(),
  recommended: MiceTravelerFlightSchema.nullable(),
  /** Preço do pedido − tarifa do recomendado; null quando o pedido é o próprio recomendado, sem os
   *  dois preços ou com moedas diferentes. */
  difference: MiceMoneySchema.nullable(),
  /** O voo que seria comprado fere uma regra do evento. */
  outsideRules: z.boolean(),
  /** Avisos de regra do voo que seria comprado (os mesmos do card do viajante). */
  warnings: z.array(MiceTravelerWarningSchema).default([]),
});
export type MiceApprovalDirection = z.infer<typeof MiceApprovalDirectionSchema>;

/** O pedido do viajante que espera o organizador, com a versão da recomendação em que nasceu. */
export const MiceParticipantApprovalSchema = z.object({
  needId: z.string(),
  version: z.number().int().positive(),
  requestedAt: z.iso.datetime(),
  justification: z.string(),
  /** Recusar manda o recomendado à compra (status `recommended` e dentro das regras). Default:
   *  view de um mice-api antigo. */
  refuseBuysRecommended: z.boolean().default(false),
  directions: z.array(MiceApprovalDirectionSchema).min(1).max(2),
});
export type MiceParticipantApproval = z.infer<typeof MiceParticipantApprovalSchema>;

/** O organizador aprova ou recusa o pedido que viu: a `version` e o `requestedAt` de
 *  `approval` (o viajante pode trocar o pedido na mesma versão). */
export const MiceApprovalDecisionRequestSchema = z.object({
  organizerId: NumericIdSchema,
  decision: z.enum(['approve', 'refuse']),
  version: z.number().int().positive(),
  requestedAt: z.iso.datetime(),
});
export type MiceApprovalDecisionRequest = z.infer<typeof MiceApprovalDecisionRequestSchema>;

export const MiceParticipantViewSchema = z.object({
  id: z.string(),
  email: z.string(),
  traveler: MiceNamedRefSchema.nullable(),
  costCenter: MiceNamedRefSchema.nullable(),
  /** O que veio na planilha para o centro de custo, quando não casou com nenhum. */
  costCenterRaw: z.string().nullable(),
  // Default: view de um mice-api antigo no rolling deploy.
  motive: MiceNamedRefSchema.nullable().default(null),
  project: MiceNamedRefSchema.nullable().default(null),
  /** O texto cru de motivo/projeto da planilha, quando não casou com nenhum. */
  motiveRaw: z.string().nullable().default(null),
  projectRaw: z.string().nullable().default(null),
  source: z.enum(['sheet', 'search']),
  sheetLine: z.number().int().nullable(),
  addedAt: z.iso.datetime(),
  addedAfterPublish: z.boolean(),
  pending: z.array(MiceParticipantPendingSchema),
  /** Default: view de um mice-api antigo no rolling deploy. */
  invitedAt: z.string().nullable().default(null),
  response: MiceInviteResponseSchema.nullable().default(null),
  /** Pedido do viajante esperando o organizador; null sem pedido pendente. */
  approval: MiceParticipantApprovalSchema.nullable().default(null),
});
export type MiceParticipantView = z.infer<typeof MiceParticipantViewSchema>;

export const MiceParticipantCountsSchema = z.object({
  total: z.number().int(),
  pending: z.number().int(),
  withCostCenter: z.number().int(),
  invited: z.number().int().default(0),
  accepted: z.number().int().default(0),
  declined: z.number().int().default(0),
});
export type MiceParticipantCounts = z.infer<typeof MiceParticipantCountsSchema>;
export const EMPTY_PARTICIPANT_COUNTS: MiceParticipantCounts = {
  total: 0,
  pending: 0,
  withCostCenter: 0,
  invited: 0,
  accepted: 0,
  declined: 0,
};

export const MiceParticipantsPageSchema = z.object({
  items: z.array(MiceParticipantViewSchema),
  counts: MiceParticipantCountsSchema,
});
export type MiceParticipantsPage = z.infer<typeof MiceParticipantsPageSchema>;

export const MiceParticipantImportSummarySchema = z.object({
  fileName: z.string(),
  lines: z.number().int(),
  added: z.number().int(),
  removed: z.number().int(),
  kept: z.number().int(),
});
export type MiceParticipantImportSummary = z.infer<typeof MiceParticipantImportSummarySchema>;

export const MiceParticipantImportResultSchema = MiceParticipantsPageSchema.extend({
  import: MiceParticipantImportSummarySchema,
});
export type MiceParticipantImportResult = z.infer<typeof MiceParticipantImportResultSchema>;

export const MiceParticipantHistoryEntrySchema = z.object({
  at: z.iso.datetime(),
  by: z.string(),
  action: z.enum([
    'added',
    'removed',
    'email_fixed',
    'cost_center_changed',
    'motive_changed',
    'project_changed',
    'sheet_replaced',
  ]),
  email: z.string().nullable(),
  before: z.record(z.string(), z.unknown()).nullable(),
  after: z.record(z.string(), z.unknown()).nullable(),
  afterPublish: z.boolean(),
});
export type MiceParticipantHistoryEntry = z.infer<typeof MiceParticipantHistoryEntrySchema>;

export const MiceParticipantHistoryPageSchema = z.object({
  items: z.array(MiceParticipantHistoryEntrySchema),
});
export type MiceParticipantHistoryPage = z.infer<typeof MiceParticipantHistoryPageSchema>;

export const SelfServiceAddParticipantRequestSchema = z.object({
  organizerId: NumericIdSchema,
  email: z.email(),
});
export type SelfServiceAddParticipantRequest = z.infer<
  typeof SelfServiceAddParticipantRequestSchema
>;

/** Corrige o e-mail OU troca o centro de custo, o motivo ou o projeto (null limpa): exatamente um. */
export const SelfServicePatchParticipantRequestSchema = z.union([
  z.object({ organizerId: NumericIdSchema, email: z.email() }).strict(),
  z.object({ organizerId: NumericIdSchema, costCenterId: z.string().min(1).nullable() }).strict(),
  z.object({ organizerId: NumericIdSchema, motiveId: z.string().min(1).nullable() }).strict(),
  z.object({ organizerId: NumericIdSchema, projectId: z.string().min(1).nullable() }).strict(),
]);
export type SelfServicePatchParticipantRequest = z.infer<
  typeof SelfServicePatchParticipantRequestSchema
>;

export const SelfServicePublishRequestSchema = SelfServiceActionRequestSchema;
export type SelfServicePublishRequest = z.infer<typeof SelfServicePublishRequestSchema>;

export const SelfServiceParticipantsIncompleteErrorSchema = z.object({
  code: z.literal('participants_incomplete'),
  count: z.number().int(),
  pending: z.number().int(),
});

/** Pendências de um participante na lista: sem viajante no cadastro, ou (caminho `per_traveler` do
 *  centro de custo) sem centro. Só a primeira trava a publicação (`participantBlocksPublish`). */
export function participantPending(
  p: { traveler: unknown; costCenter: unknown; costCenterRaw: string | null },
  path: MiceCorporatePath,
): MiceParticipantPending[] {
  const pending: MiceParticipantPending[] = [];
  if (p.traveler === null) pending.push('email_not_found');
  if (path === 'per_traveler' && p.costCenter === null) {
    pending.push(p.costCenterRaw ? 'cost_center_not_found' : 'cost_center_missing');
  }
  return pending;
}

/** O que trava a primeira publicação (e conta em `counts.pending`): só o e-mail fora do cadastro.
 *  Centro de custo, motivo e projeto vazios no `per_traveler` não travam: o convite pede ao
 *  viajante. */
export function participantBlocksPublish(p: { traveler: unknown }): boolean {
  return p.traveler === null;
}

/** Pendências do texto cru de motivo/projeto que não casou com o catálogo. Informativas: não entram
 *  em `participantPending` nem bloqueiam a publicação. */
export function corporateRawPending(p: {
  motiveRaw: string | null;
  projectRaw: string | null;
}): MiceParticipantPending[] {
  const pending: MiceParticipantPending[] = [];
  if (p.motiveRaw) pending.push('motive_not_found');
  if (p.projectRaw) pending.push('project_not_found');
  return pending;
}
